// ============================================================
//  src/pages/CollaborationsPage.js
//  Styles: ./CollaborationsPage.css (scoped under .clb-*)
// ============================================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  listCollabs, listReceivedInvites, inviteCollaborator,
  respondToInvite, removeCollaborator, updateCollabAccess,
  listCollabBots, listBots, getBillingOverview,
} from '../api/client';
import toast from 'react-hot-toast';
import './CollaborationsPage.css';

// ── Permission definitions ────────────────────────────────────
const ALL_PERMS = [
  { key: 'commands',  icon: 'fa-terminal',         label: 'Commands',  desc: 'View & edit bot commands' },
  { key: 'panel',     icon: 'fa-table-columns',    label: 'Panel',     desc: 'View & edit panel config' },
  { key: 'flows',     icon: 'fa-diagram-project',  label: 'Flows',     desc: 'Build & publish flows' },
  { key: 'users',     icon: 'fa-users',            label: 'Users',     desc: 'View bot users' },
  { key: 'logs',      icon: 'fa-chart-line',       label: 'Logs',      desc: 'View runtime logs' },
  { key: 'endpoints', icon: 'fa-plug',             label: 'Endpoints', desc: 'Create & run custom code endpoints' },
  { key: 'settings',  icon: 'fa-gear',             label: 'Settings',  desc: 'Edit bot settings & credentials' },
];

const FULL_ACCESS = ALL_PERMS.map(p => p.key);

const ACCESS_PRESETS = [
  { id: 'full',   label: 'Full access', icon: 'fa-crown',       desc: 'Everything the owner can do',       perms: FULL_ACCESS },
  { id: 'editor', label: 'Editor',      icon: 'fa-pen-nib',     desc: 'Build commands, panel & flows',     perms: ['commands', 'panel', 'flows', 'users', 'logs'] },
  { id: 'viewer', label: 'Viewer',      icon: 'fa-eye',         desc: 'Read-only: users and logs',         perms: ['users', 'logs'] },
  { id: 'custom', label: 'Custom',      icon: 'fa-sliders',     desc: 'Hand-picked permissions',           perms: null },
];

function presetFromPerms(perms = []) {
  const sorted = [...perms].sort().join(',');
  for (const p of ACCESS_PRESETS) {
    if (p.perms && [...p.perms].sort().join(',') === sorted) return p.id;
  }
  return 'custom';
}
const presetOf = perms => ACCESS_PRESETS.find(p => p.id === presetFromPerms(perms ?? []));

// ── Helpers ───────────────────────────────────────────────────
const AV_COLORS = ['#0E9F6E', '#2563EB', '#7C3AED', '#DB2777', '#EA580C', '#0891B2', '#4F46E5', '#059669'];
function avColor(str = '') {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
}
function initialsOf(name = '') {
  const clean = String(name || '?').split('@')[0].replace(/[._-]+/g, ' ');
  return clean.split(' ').filter(Boolean).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}
function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(String(d).replace(' ', 'T'));
  return isNaN(dt) ? '' : dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Avatar({ name, size = 'md' }) {
  return <span className={`clb-av ${size}`} style={{ background: avColor(name) }} aria-hidden="true">{initialsOf(name)}</span>;
}
function BotTile({ name, size = 'md' }) {
  return <span className={`clb-bot-tile ${size}`} style={{ '--tile': avColor(name || '') }} aria-hidden="true">{(name || '?')[0]?.toUpperCase()}</span>;
}
function RolePill({ perms }) {
  const p = presetOf(perms);
  return <span className={`clb-role is-${p.id}`}><i className={`fa-solid ${p.icon}`} /> {p.label}</span>;
}
function PermChips({ perms = [], compact = false }) {
  return (
    <div className={`clb-chips ${compact ? 'is-compact' : ''}`}>
      {ALL_PERMS.filter(a => perms.includes(a.key)).map(def => (
        <span key={def.key} className="clb-chip" title={def.desc}>
          <i className={`fa-solid ${def.icon}`} />{!compact && <> {def.label}</>}
        </span>
      ))}
    </div>
  );
}
function EmptyState({ icon, title, sub, action }) {
  return (
    <div className="clb-empty">
      <span className="clb-empty-ic"><i className={`fa-solid ${icon}`} /></span>
      <b>{title}</b>
      <span>{sub}</span>
      {action}
    </div>
  );
}

// ── Modal shell (centred on desktop, bottom sheet on mobile) ──
function ModalShell({ icon, title, sub, onClose, busy, children, footer }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = e => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener('keydown', onKey); };
  }, [onClose, busy]);

  return (
    <div className="clb-backdrop" onClick={() => !busy && onClose()}>
      <div className="clb-modal" role="dialog" aria-label={title} onClick={e => e.stopPropagation()}>
        <span className="clb-grip" />
        <div className="clb-modal-head">
          <span className="clb-modal-ic"><i className={`fa-solid ${icon}`} /></span>
          <div><h3>{title}</h3>{sub && <p>{sub}</p>}</div>
          <button type="button" className="clb-icon-btn ghost" onClick={onClose} aria-label="Close"><i className="fa-solid fa-xmark" /></button>
        </div>
        <div className="clb-modal-body">{children}</div>
        <div className="clb-modal-foot">{footer}</div>
      </div>
    </div>
  );
}

// ── Permission picker ─────────────────────────────────────────
function PermissionPicker({ value, onChange }) {
  const preset = presetFromPerms(value);

  function togglePerm(key) {
    if (value.includes(key)) onChange(value.filter(k => k !== key));
    else onChange([...value, key]);
  }

  return (
    <div className="clb-picker">
      <div className="clb-presets" role="radiogroup" aria-label="Access level">
        {ACCESS_PRESETS.filter(p => p.id !== 'custom').map(p => (
          <button key={p.id} type="button" role="radio" aria-checked={preset === p.id}
            className={`clb-preset is-${p.id} ${preset === p.id ? 'is-active' : ''}`} onClick={() => onChange(p.perms)}>
            <span className="clb-preset-ic"><i className={`fa-solid ${p.icon}`} /></span>
            <b>{p.label}</b>
            <span>{p.desc}</span>
            <i className="fa-solid fa-circle-check clb-preset-check" />
          </button>
        ))}
      </div>

      <div className="clb-perm-head">
        <span>Permissions</span>
        <em>{preset === 'custom' && value.length > 0 ? 'Custom · ' : ''}{value.length} of {ALL_PERMS.length}</em>
      </div>
      <div className="clb-perms">
        {ALL_PERMS.map(p => {
          const on = value.includes(p.key);
          return (
            <label key={p.key} className={`clb-perm ${on ? 'is-on' : ''}`}>
              <span className="clb-perm-ic"><i className={`fa-solid ${p.icon}`} /></span>
              <span className="clb-perm-text"><b>{p.label}</b><span>{p.desc}</span></span>
              <input type="checkbox" checked={on} onChange={() => togglePerm(p.key)} />
              <span className="clb-switch" aria-hidden="true" />
            </label>
          );
        })}
      </div>

      {value.includes('settings') && (
        <div className="clb-warn">
          <i className="fa-solid fa-triangle-exclamation" />
          <span><b>Settings</b> lets this person see and change your bot’s credentials. Only grant it to people you fully trust.</span>
        </div>
      )}
    </div>
  );
}

// ── Invite modal ──────────────────────────────────────────────
function InviteModal({ bots, initialBotId, onClose, onInvited }) {
  const [email, setEmail] = useState('');
  const [bId, setBId] = useState(initialBotId ?? bots[0]?.bId ?? '');
  const [perms, setPerms] = useState(['commands', 'panel', 'flows', 'users', 'logs']);
  const [loading, setLoading] = useState(false);

  async function submit() {
    if (!email.trim()) return toast.error('Enter the collaborator\'s email.');
    if (!bId) return toast.error('Select a bot.');
    if (!perms.length) return toast.error('Select at least one permission.');
    setLoading(true);
    try {
      const { data } = await inviteCollaborator({ email: email.trim(), bId, permissions: perms });
      if (data?.success) {
        toast.success('Invitation sent!');
        onInvited();
        onClose();
      } else {
        toast.error(data?.message || 'Failed to send invitation.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send invitation.');
    }
    setLoading(false);
  }

  return (
    <ModalShell
      icon="fa-user-plus" title="Invite a collaborator" sub="They’ll get an email and can accept from their Collaborations page."
      onClose={onClose} busy={loading}
      footer={<>
        <button type="button" className="clb-btn ghost" onClick={onClose} disabled={loading}>Cancel</button>
        <button type="button" className="clb-btn primary" onClick={submit} disabled={loading}>
          {loading ? <><span className="clb-spin" /> Sending…</> : <><i className="fa-solid fa-paper-plane" /> Send invitation</>}
        </button>
      </>}
    >
      <div className="clb-field">
        <label htmlFor="clb-email">Email address</label>
        <div className="clb-input-wrap">
          <i className="fa-regular fa-envelope" />
          <input id="clb-email" className="clb-input" type="email" placeholder="name@example.com"
            value={email} onChange={e => setEmail(e.target.value)} autoFocus
            onKeyDown={e => { if (e.key === 'Enter') submit(); }} />
        </div>
      </div>

      <div className="clb-field">
        <label htmlFor="clb-bot">Bot</label>
        <div className="clb-input-wrap is-select">
          <i className="fa-solid fa-robot" />
          <select id="clb-bot" className="clb-input" value={bId} onChange={e => setBId(e.target.value)}>
            {bots.map(b => <option key={b.bId} value={b.bId}>{b.bName}</option>)}
          </select>
          <i className="fa-solid fa-chevron-down clb-caret" />
        </div>
      </div>

      <div className="clb-field">
        <label>Access level</label>
        <PermissionPicker value={perms} onChange={setPerms} />
      </div>
    </ModalShell>
  );
}

// ── Edit access modal ─────────────────────────────────────────
function EditAccessModal({ collab, onClose, onSaved }) {
  const [perms, setPerms] = useState(collab.permissions ?? []);
  const [loading, setLoading] = useState(false);

  async function save() {
    if (!perms.length) return toast.error('Select at least one permission.');
    setLoading(true);
    try {
      const { data } = await updateCollabAccess(collab.cId, perms);
      if (data?.success) { toast.success('Access updated.'); onSaved(); onClose(); }
      else toast.error(data?.message || 'Failed to update.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update.');
    }
    setLoading(false);
  }

  return (
    <ModalShell
      icon="fa-user-shield" title="Edit access"
      sub={<>{collab.name || collab.email} · <b>{collab.botName ?? `Bot #${collab.bId}`}</b></>}
      onClose={onClose} busy={loading}
      footer={<>
        <button type="button" className="clb-btn ghost" onClick={onClose} disabled={loading}>Cancel</button>
        <button type="button" className="clb-btn primary" onClick={save} disabled={loading}>
          {loading ? <><span className="clb-spin" /> Saving…</> : <><i className="fa-solid fa-check" /> Save changes</>}
        </button>
      </>}
    >
      <PermissionPicker value={perms} onChange={setPerms} />
    </ModalShell>
  );
}

// ── Collaborator row (my bots → outgoing) ─────────────────────
const STATUS = {
  pending:  { cls: 'is-pending',  label: 'Pending',  icon: 'fa-clock' },
  active:   { cls: 'is-active',   label: 'Active',   icon: 'fa-circle-check' },
  declined: { cls: 'is-declined', label: 'Declined', icon: 'fa-circle-xmark' },
};

function CollabRow({ collab, onRemove, onEdit }) {
  const [removing, setRemoving] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const sc = STATUS[collab.status] ?? STATUS.pending;

  async function handleRemove() {
    setRemoving(true);
    try {
      const { data } = await removeCollaborator(collab.cId);
      if (data?.success) { toast.success('Collaborator removed.'); onRemove(collab.cId); }
      else toast.error(data?.message || 'Failed to remove.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to remove.');
    }
    setRemoving(false);
    setConfirm(false);
  }

  return (
    <div className={`clb-row ${confirm ? 'is-confirming' : ''}`}>
      <div className="clb-row-main">
        <Avatar name={collab.name || collab.email} />
        <div className="clb-row-who">
          <b>{collab.name || collab.email}</b>
          {collab.name && <span>{collab.email}</span>}
        </div>
      </div>
      <div className="clb-row-meta">
        <RolePill perms={collab.permissions} />
        <span className={`clb-status ${sc.cls}`}><i className={`fa-solid ${sc.icon}`} /> {sc.label}</span>
      </div>
      <div className="clb-row-actions">
        {confirm ? (
          <span className="clb-inline-confirm">
            Remove access?
            <button type="button" className="clb-btn sm danger-solid" onClick={handleRemove} disabled={removing}>
              {removing ? <span className="clb-spin" /> : 'Remove'}
            </button>
            <button type="button" className="clb-btn sm ghost" onClick={() => setConfirm(false)} disabled={removing}>Cancel</button>
          </span>
        ) : (
          <>
            {collab.status !== 'declined' && (
              <button type="button" className="clb-btn sm ghost" onClick={() => onEdit(collab)}>
                <i className="fa-solid fa-sliders" /> Edit access
              </button>
            )}
            <button type="button" className="clb-icon-btn danger" onClick={() => setConfirm(true)} aria-label={`Remove ${collab.email}`} title="Remove">
              <i className="fa-regular fa-trash-can" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Invite card (received — pending) ─────────────────────────
function InviteCard({ invite, onRespond }) {
  const [loading, setLoading] = useState(null);

  async function respond(accept) {
    setLoading(accept ? 'accept' : 'decline');
    try {
      const { data } = await respondToInvite(invite.cId, accept);
      if (data?.success) {
        toast.success(accept ? 'Invitation accepted!' : 'Invitation declined.');
        onRespond(invite.cId);
      } else {
        toast.error(data?.message || 'Failed to respond.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to respond.');
    }
    setLoading(null);
  }

  const botName = invite.botName ?? `Bot #${invite.bId}`;
  return (
    <article className="clb-card clb-invite">
      <div className="clb-invite-top">
        <BotTile name={botName} size="lg" />
        <div className="clb-invite-copy">
          <span className="clb-invite-kicker"><span className="clb-pulse" /> New invitation</span>
          <h3>{botName}</h3>
          <p>
            <b>{invite.ownerName || invite.ownerEmail}</b> invited you to collaborate
            {invite.invitedAt && fmtDate(invite.invitedAt) && <> · {fmtDate(invite.invitedAt)}</>}
          </p>
        </div>
        <RolePill perms={invite.permissions} />
      </div>
      <div className="clb-invite-perms">
        <span className="clb-mini-label">You’ll be able to access</span>
        <PermChips perms={invite.permissions ?? []} />
      </div>
      <div className="clb-invite-actions">
        <button type="button" className="clb-btn ghost danger" onClick={() => respond(false)} disabled={!!loading}>
          {loading === 'decline' ? <span className="clb-spin" /> : 'Decline'}
        </button>
        <button type="button" className="clb-btn primary" onClick={() => respond(true)} disabled={!!loading}>
          {loading === 'accept' ? <><span className="clb-spin" /> Accepting…</> : <><i className="fa-solid fa-check" /> Accept invitation</>}
        </button>
      </div>
    </article>
  );
}

// ── Shared bot card (bots shared with me — active) ────────────
function SharedBotCard({ bot, navigate }) {
  const open = () => navigate(`/bots/${bot.bId}`);
  return (
    <article className="clb-card clb-shared" onClick={open} role="button" tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') open(); }}>
      <div className="clb-shared-top">
        <BotTile name={bot.bName} />
        <div className="clb-shared-name">
          <b>{bot.bName}</b>
          <span><i className="fa-regular fa-user" /> {bot.ownerName || bot.ownerEmail}</span>
        </div>
        <span className="clb-go"><i className="fa-solid fa-arrow-right" /></span>
      </div>
      <div className="clb-shared-foot">
        <RolePill perms={bot.permissions} />
        <PermChips perms={bot.permissions ?? []} compact />
      </div>
    </article>
  );
}

// ── Roles reference (side panel) ──────────────────────────────
function RolesGuide() {
  const roles = ACCESS_PRESETS.filter(p => p.perms);
  return (
    <div className="clb-card clb-guide">
      <h4><i className="fa-solid fa-user-shield" /> Roles at a glance</h4>
      <div className="clb-matrix" role="table" aria-label="Permissions by role">
        <div className="clb-matrix-row is-head" role="row">
          <span role="columnheader" />
          {roles.map(r => <span key={r.id} role="columnheader" className={`is-${r.id}`}>{r.label.split(' ')[0]}</span>)}
        </div>
        {ALL_PERMS.map(p => (
          <div key={p.key} className="clb-matrix-row" role="row">
            <span role="rowheader"><i className={`fa-solid ${p.icon}`} /> {p.label}</span>
            {roles.map(r => (
              <span key={r.id} role="cell" className={r.perms.includes(p.key) ? 'is-yes' : 'is-no'}>
                <i className={`fa-solid ${r.perms.includes(p.key) ? 'fa-check' : 'fa-minus'}`} />
              </span>
            ))}
          </div>
        ))}
      </div>
      <p className="clb-guide-note">You stay the owner of your bot. Change or remove someone’s access any time from My collaborators.</p>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function CollaborationsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab'); // deep link from invite email, e.g. ?tab=pending

  const [tab, setTab] = useState(
    ['shared', 'sent', 'pending'].includes(requestedTab) ? requestedTab : 'shared'
  ); // shared | sent | pending
  const [myBots, setMyBots] = useState([]);
  const [sentInvites, setSentInvites] = useState([]);   // invites I sent
  const [pending, setPending] = useState([]);           // invites received
  const [sharedBots, setSharedBots] = useState([]);     // bots shared WITH me (active)
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteBotId, setInviteBotId] = useState(null);
  const [editingCollab, setEditingCollab] = useState(null);
  const [canCollab, setCanCollab] = useState(true);     // plan gate

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sentRes, receivedRes, sharedRes, botsRes, billingRes] = await Promise.allSettled([
        listCollabs(),
        listReceivedInvites(),
        listCollabBots(),
        listBots(),
        getBillingOverview(),
      ]);

      if (sentRes.status === 'fulfilled') setSentInvites(sentRes.value?.data?.collabs ?? []);
      if (receivedRes.status === 'fulfilled') {
        const invites = receivedRes.value?.data?.invites ?? [];
        setPending(invites);
        // Safety net: if no tab was explicitly requested (e.g. an old email link)
        // and there's a pending invite waiting, surface it automatically.
        if (!requestedTab && invites.some(i => i.status === 'pending')) {
          setTab('pending');
        }
      }
      if (sharedRes.status === 'fulfilled') setSharedBots(sharedRes.value?.data?.bots ?? []);
      if (botsRes.status === 'fulfilled') setMyBots(botsRes.value?.data?.bots ?? []);
      if (billingRes.status === 'fulfilled') {
        const plan = billingRes.value?.data?.current_plan;
        setCanCollab(plan?.plAllowCollaboration !== false ? !!plan?.plAllowCollaboration : false);
      }
    } catch { /* handled per-call */ }
    setLoading(false);
  }, [requestedTab]);

  useEffect(() => { load(); }, [load]);

  const pendingList = pending.filter(i => i.status === 'pending');
  const pastList = pending.filter(i => i.status !== 'pending');
  const pendingCount = pendingList.length;
  const noBots = myBots.length === 0;

  const groups = useMemo(() => Object.entries(
    sentInvites.reduce((acc, c) => {
      const key = c.bId;
      if (!acc[key]) acc[key] = { botName: c.botName ?? c.bId, items: [] };
      acc[key].items.push(c);
      return acc;
    }, {})
  ), [sentInvites]);

  const activeCollabs = sentInvites.filter(c => c.status === 'active').length;

  function startInvite(botId = null) {
    if (!canCollab) { navigate('/billing'); return; }
    if (noBots) { toast.error('Create a bot first, then invite people to it.'); navigate('/dashboard'); return; }
    setInviteBotId(botId);
    setShowInvite(true);
  }
  const closeInvite = useCallback(() => setShowInvite(false), []);
  const closeEdit = useCallback(() => setEditingCollab(null), []);

  const tabs = [
    { id: 'shared',  icon: 'fa-handshake',      label: 'Shared with me',   short: 'Shared',      count: sharedBots.length },
    { id: 'pending', icon: 'fa-envelope-open-text', label: 'Invitations',  short: 'Invites',     count: pendingCount, alert: pendingCount > 0 },
    { id: 'sent',    icon: 'fa-user-group',     label: 'My collaborators', short: 'My team',     count: sentInvites.length },
  ];

  const inviteBtnLabel = !canCollab
    ? <><i className="fa-solid fa-lock" /> Upgrade to invite</>
    : <><i className="fa-solid fa-user-plus" /> Invite collaborator</>;

  return (
    <div className="clb">
      {/* Header */}
      <header className="clb-head">
        <div className="clb-head-copy">
          <span className="clb-eyebrow"><i className="fa-solid fa-user-group" /> Team</span>
          <h1>Collaborations</h1>
          <p>Bring people in to help build and run your bots, with exactly the access you choose — or work on bots others have shared with you.</p>
        </div>
        <button type="button" className={`clb-btn ${canCollab ? 'primary' : 'dark'} lg`} onClick={() => startInvite()}
          title={!canCollab ? 'Upgrade your plan to invite collaborators' : noBots ? 'Create a bot first' : ''}>
          {inviteBtnLabel}
        </button>
      </header>

      {!canCollab && (
        <div className="clb-banner" role="status">
          <span className="clb-banner-ic"><i className="fa-solid fa-lock" /></span>
          <div>
            <b>Inviting isn’t included in your plan</b>
            <span>You can still accept invitations and work on bots shared with you.</span>
          </div>
          <button type="button" className="clb-btn dark sm" onClick={() => navigate('/billing')}>See plans</button>
        </div>
      )}

      {/* Tabs */}
      <div className="clb-tabs" role="tablist" aria-label="Collaboration sections">
        {tabs.map(t => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
            className={`clb-tab ${tab === t.id ? 'is-active' : ''}`} onClick={() => setTab(t.id)}>
            <span className="clb-tab-ic"><i className={`fa-solid ${t.icon}`} /></span>
            <span className="clb-tab-text">
              <span className="clb-tab-label"><span className="long">{t.label}</span><span className="short">{t.short}</span></span>
              <b>{loading ? '–' : t.count}</b>
            </span>
            {t.alert && !loading && <span className="clb-tab-dot" aria-label="Needs attention" />}
          </button>
        ))}
      </div>

      <div className="clb-layout">
        <section className="clb-main">
          {loading ? (
            <div className="clb-skel-list">
              {[0, 1, 2].map(i => (
                <div key={i} className="clb-card clb-skel"><span className="sk circle" /><div><span className="sk w40" /><span className="sk w70" /></div></div>
              ))}
            </div>
          ) : (
            <>
              {/* ── Tab: Shared with me ── */}
              {tab === 'shared' && (
                sharedBots.length === 0
                  ? <EmptyState icon="fa-handshake" title="No shared bots yet"
                      sub="When someone invites you to their bot and you accept, it shows up here so you can jump straight in."
                      action={pendingCount > 0 && (
                        <button type="button" className="clb-btn dark" onClick={() => setTab('pending')}>
                          View {pendingCount} invitation{pendingCount !== 1 ? 's' : ''}
                        </button>
                      )} />
                  : (
                    <>
                      <p className="clb-section-note">{sharedBots.length} bot{sharedBots.length !== 1 ? 's' : ''} shared with you. Tap one to open it.</p>
                      <div className="clb-shared-grid">
                        {sharedBots.map(b => <SharedBotCard key={b.bId + '-' + b.cId} bot={b} navigate={navigate} />)}
                      </div>
                    </>
                  )
              )}

              {/* ── Tab: Invitations received ── */}
              {tab === 'pending' && (
                pending.length === 0
                  ? <EmptyState icon="fa-envelope-open-text" title="No invitations"
                      sub="When someone invites you to collaborate on their bot, you’ll be able to accept it here." />
                  : (
                    <div className="clb-stack">
                      {pendingList.length > 0 && (
                        <>
                          <h2 className="clb-group-label">Waiting for you <em>{pendingList.length}</em></h2>
                          {pendingList.map(inv => <InviteCard key={inv.cId} invite={inv} onRespond={() => load()} />)}
                        </>
                      )}
                      {pastList.length > 0 && (
                        <>
                          <h2 className="clb-group-label is-past">Past invitations</h2>
                          <div className="clb-card clb-past">
                            {pastList.map(inv => (
                              <div key={inv.cId} className="clb-past-row">
                                <BotTile name={inv.botName ?? `Bot #${inv.bId}`} size="sm" />
                                <div className="clb-row-who">
                                  <b>{inv.botName ?? `Bot #${inv.bId}`}</b>
                                  <span>From {inv.ownerName || inv.ownerEmail}</span>
                                </div>
                                <span className="clb-status is-muted">{inv.status === 'declined' ? 'Declined' : 'Inactive'}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  )
              )}

              {/* ── Tab: My collaborators (sent) ── */}
              {tab === 'sent' && (
                sentInvites.length === 0
                  ? (
                    <EmptyState icon="fa-user-plus" title="No collaborators yet"
                      sub="Invite a teammate, client or developer to one of your bots. You decide exactly what they can see and change."
                      action={<button type="button" className={`clb-btn ${canCollab ? 'primary' : 'dark'}`} onClick={() => startInvite()}>{inviteBtnLabel}</button>} />
                  ) : (
                    <div className="clb-stack">
                      <p className="clb-section-note">
                        {sentInvites.length} collaborator{sentInvites.length !== 1 ? 's' : ''} across {groups.length} bot{groups.length !== 1 ? 's' : ''}
                        {activeCollabs !== sentInvites.length && <> · {activeCollabs} active</>}
                      </p>
                      {groups.map(([bId, group]) => (
                        <div key={bId} className="clb-card clb-group">
                          <div className="clb-group-head">
                            <BotTile name={String(group.botName)} size="sm" />
                            <div className="clb-row-who">
                              <b>{group.botName}</b>
                              <span>{group.items.length} collaborator{group.items.length !== 1 ? 's' : ''}</span>
                            </div>
                            {canCollab && (
                              <button type="button" className="clb-btn sm ghost" onClick={() => startInvite(bId)}>
                                <i className="fa-solid fa-plus" /> <span>Invite</span>
                              </button>
                            )}
                          </div>
                          {group.items.map(c => (
                            <CollabRow
                              key={c.cId}
                              collab={c}
                              onRemove={cId => setSentInvites(prev => prev.filter(x => x.cId !== cId))}
                              onEdit={c => setEditingCollab(c)}
                            />
                          ))}
                        </div>
                      ))}
                    </div>
                  )
              )}
            </>
          )}
        </section>

        <aside className="clb-side">
          <RolesGuide />
        </aside>
      </div>

      {/* Modals */}
      {showInvite && (
        <InviteModal
          bots={myBots}
          initialBotId={inviteBotId}
          onClose={closeInvite}
          onInvited={load}
        />
      )}

      {editingCollab && (
        <EditAccessModal
          collab={editingCollab}
          onClose={closeEdit}
          onSaved={load}
        />
      )}
    </div>
  );
}
